/**
 * Centralized copy for the Credits tab inside Customer Information
 * (abo-member-credits): the balance, the ledger history and the role-gated
 * add/remove panel. Kept out of the components per the repo rule that feature
 * text is not hardcoded inline (mirrors MemberSubscriptionsTab.strings.ts).
 * Placeholders use `{token}` and are filled with `formatMessage`.
 */
export { formatMessage } from "./MemberBooksTab.strings";

export const memberCreditsStrings = {
  tabTitle: "Credits",
  none: "—",
  loadError: "Could not load this member's credits. Please try again.",
  retry: "Try again",
  balance: {
    heading: "Balance",
    printCredits: "Print credits",
    lastMovement: "Last movement",
    lastMovementValue: "{change} · {date}",
    ledgerLive: "Ledger is live",
    ledgerNotLive: "Ledger not live yet",
    adjustButton: "Adjust credits",
  },
  notice: {
    notAuthoritative:
      "This is the DS credits ledger. Until the credits cutover the member's spendable balance may still come from HubSpot.",
  },
  history: {
    heading: "Credit history",
    empty: "No credit movements yet",
    emptyFiltered: "No movements of this type",
    columns: {
      date: "Date",
      movement: "Movement",
      change: "Change",
      balanceAfter: "Balance",
      details: "Details",
    },
    filters: {
      label: "Filter movements",
      all: "All",
      grants: "Grants",
      spend: "Spend",
      adjustments: "Adjustments",
    },
    byOperator: "by {operator}",
    requested: "Requested {amount}",
    reasons: {
      purchase: "Purchase",
      subscription_credit_grant: "Subscription grant",
      book_print_debit: "Book print",
      mod_order_debit: "Mail on Demand order",
      refund: "Refund",
      adjustment: "Operator adjustment",
      downgrade_clawback: "Downgrade clawback",
    } as Record<string, string>,
    refTypes: {
      mod_order: "MOD order",
      book_order: "Book order",
      chargebee_invoice: "Chargebee invoice",
      subscription_event: "Subscription event",
      manual: "Manual",
      operator_adjustment: "Adjustment request",
    } as Record<string, string>,
    pagination: {
      previous: "Previous",
      next: "Next",
      showing: "Showing {from}–{to} of {total}",
      label: "Credit history pages",
    },
  },
  adjust: {
    heading: "Adjust credits",
    subtitle: "{name} · up to {max} per adjustment",
    close: "Close",
    add: "Add credits",
    remove: "Remove credits",
    directionLabel: "Adjustment type",
    amountLabel: "Amount",
    amountHint: "Whole number from 1 to {max}.",
    amountLimitReached: "Limit reached: {max} is the most per adjustment.",
    quickAmounts: "Quick amounts",
    reasonLabel: "Reason",
    reasonCategories: {
      goodwill: "Goodwill",
      missingGrant: "Missing grant",
      duplicateGrant: "Duplicate grant",
      misprint: "Misprint",
      other: "Other",
    },
    noteLabel: "Note",
    noteHint: "shown in the history",
    reasonPlaceholder: "What happened? Order, ticket, invoice…",
    reasonCounter: "{count}/{max}",
    preview: {
      now: "Balance now",
      after: "After",
    },
    review: "Review",
    reviewAdd: "Review: add {amount}",
    reviewRemove: "Review: remove {amount}",
    recordedAs: "Recorded as {email} (verified sign-in)",
    validation: {
      amount: "Enter a whole number from 1 to {max}.",
      reasonRequired: "Pick a reason or write a note.",
      reasonTooLong: "The reason can be at most {max} characters.",
    },
    confirm: {
      title: "Confirm credit adjustment",
      addBody: "Add {amount} to {name}?",
      removeBody: "Remove {amount} from {name}?",
      balanceChange: "Balance now {before} → {after}.",
      clampWarning:
        "The member has only {balance}: at most {balance} will be removed.",
      reason: "Reason: {reason}",
      confirm: "Confirm",
      retry: "Try again",
      cancel: "Back",
    },
    credits: {
      one: "{count} credit",
      many: "{count} credits",
    },
    result: {
      added: "Added {amount}. New balance: {balance}.",
      removed: "Removed {amount}. New balance: {balance}.",
      clamped:
        "Removed {applied} of {requested} requested: the member only had {applied}. New balance: {balance}.",
      clampedToZero:
        "Nothing removed: the member had no credits. Balance: {balance}.",
      replayed:
        "This adjustment was already applied. Balance: {balance}.",
    },
    refusals: {
      CREDITS_ADJUST_OPERATOR_UNVERIFIED: "Your session expired.",
      CREDITS_ADJUST_FORBIDDEN:
        "You do not have permission to adjust credits.",
      CUSTOMER_NOT_FOUND: "Customer not found.",
      CREDITS_LEDGER_NOT_AUTHORITATIVE:
        "Adjustments are disabled until the credits cutover.",
      CREDITS_ADJUST_KEY_CONFLICT:
        "This request conflicts with an earlier one. Start again.",
      CREDITS_ADJUST_BALANCE_CHANGED: "The balance changed. Try again.",
      CREDITS_ADJUST_UNAVAILABLE:
        "Credits adjustment is unavailable right now. Try again later.",
    },
    signInAgain: "Sign in again",
    invalid: "Digital Services rejected the values. Check the amount and the reason.",
    error: "Could not adjust credits. Please try again.",
  },
} as const;
